import { Menu } from '@arco-design/web-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

export interface SidebarNavGroup {
  group: string
  label: string
  items: Array<{
    key: string
    label: string
    icon: ReactNode
  }>
}

export interface SidebarNavProps {
  brandName: string
  groupedItems: SidebarNavGroup[]
  selectedMenuKey: string
  collapsed?: boolean
  variant?: 'sidebar' | 'drawer'
  onNavigate: (key: string) => void
  onBrandClick?: () => void
  footer?: ReactNode
}

export default function SidebarNav({
  brandName,
  groupedItems,
  selectedMenuKey,
  collapsed = false,
  variant = 'sidebar',
  onNavigate,
  onBrandClick,
  footer,
}: SidebarNavProps) {
  const { t } = useTranslation()
  const showBrandHeader = variant === 'sidebar'

  return (
    <>
      {showBrandHeader && (
        <div className="console-sidebar-brand h-14 px-3 flex items-center border-b border-[var(--console-divider)] flex-shrink-0">
          <button
            type="button"
            className={`w-full flex items-center border-0 bg-transparent p-0 cursor-pointer ${
              collapsed ? 'justify-center' : 'justify-start gap-2.5'
            }`}
            onClick={onBrandClick}
            aria-label={brandName}
          >
            <img src="/logo.svg" alt={brandName} className="w-6 h-6 flex-shrink-0" />
            {!collapsed && (
              <div className="min-w-0 text-left">
                <span className="block text-[15px] font-bold text-[var(--console-text)] tracking-[-0.01em] leading-none truncate">
                  {brandName}
                </span>
                <span className="block text-[10px] text-[var(--console-text-muted)] mt-0.5 truncate">
                  {t('layout.consoleTagline')}
                </span>
              </div>
            )}
          </button>
        </div>
      )}

      <div className="flex-1 px-1.5 pt-1 pb-2 overflow-y-auto console-sidebar-scroll">
        {groupedItems.map((group, groupIndex) => (
          <div key={group.group} className={groupIndex > 0 ? 'mt-2' : ''}>
            {!collapsed && <div className="saas-menu-group-label">{group.label}</div>}
            {collapsed && groupIndex > 0 && <div className="saas-menu-group-divider" aria-hidden />}
            <Menu
              theme="light"
              className="saas-menu"
              mode="vertical"
              selectedKeys={[selectedMenuKey]}
              onClickMenuItem={(key) => {
                if (key.startsWith('/')) onNavigate(key)
              }}
            >
              {group.items.map((item) => (
                <Menu.Item key={item.key} title={collapsed ? item.label : undefined}>
                  <span className={`saas-menu-item-content ${collapsed ? 'is-collapsed' : ''}`}>
                    <span className="saas-menu-item-icon-wrapper">
                      <span className="saas-menu-item-icon">{item.icon}</span>
                    </span>
                    {!collapsed && (
                      <span className="saas-menu-item-label truncate">{item.label}</span>
                    )}
                  </span>
                </Menu.Item>
              ))}
            </Menu>
          </div>
        ))}
      </div>

      {footer}
    </>
  )
}
